import { useCallback, useEffect } from 'react';
import { create } from 'zustand';
import { Bug, Lightbulb } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getCapture } from '../modules/bug-report/capture';
import { useAuthStore } from '../store/authStore';
import BugReportModal from './BugReportModal';
import Dialog from './ui/Dialog';
import type { BugReportKind } from '../types';
import RegionSelector, { drawMarkedScreenshot, type Region } from './bugReport/RegionSelector';

/**
 * "דיווח על בעיה" (D-323). Entry points: the fixed button (every OTE employee), Alt+B and the avatar-menu
 * item (M3, T-30). Flow: capture the page -> the user marks the problem area (or "כל המסך") -> a short form
 * (write / dictate / voice note) -> BugReportModal sends it with the full context.
 *
 * D-341: the entry first asks "דיווח על באג" (the flow above) or "הצעה לשיפור" (no capture: straight to the form).
 * The screenshot is taken only after the chooser is closed and the menu/sheet that triggered it has finished its
 * exit motion, so the screenshot shows the page, not the menu or the chooser.
 */

type Phase = 'idle' | 'choose' | 'capturing' | 'select' | 'form';

interface BugReportState {
  phase: Phase;
  kind: BugReportKind;
  full: Blob | null;
  marked: Blob | null;
  region: Region | null;
  /** Opens the chooser (bug / suggestion). */
  start: () => void;
  choose: (kind: BugReportKind) => Promise<void>;
  confirmRegion: (region: Region | null) => Promise<void>;
  close: () => void;
}

const MENU_EXIT_MS = 260;

export const useBugReport = create<BugReportState>((set, get) => ({
  phase: 'idle',
  kind: 'bug',
  full: null,
  marked: null,
  region: null,
  start: () => {
    if (get().phase !== 'idle') return;
    set({ phase: 'choose', kind: 'bug' });
  },
  choose: async (kind) => {
    if (get().phase !== 'choose') return;
    if (kind === 'suggestion') {
      set({ phase: 'form', kind, full: null, marked: null, region: null });
      return;
    }
    set({ phase: 'capturing', kind });
    await new Promise((r) => setTimeout(r, MENU_EXIT_MS));
    let full: Blob | null = null;
    try {
      const capture = getCapture();
      if (capture) full = (await capture.captureScreenshot()).blob;
    } catch {
      // Screenshot failed: report anyway, without it.
    }
    // Without a screenshot there is nothing to mark: straight to the form.
    set(full ? { phase: 'select', full, marked: null, region: null } : { phase: 'form', full: null, marked: null, region: null });
  },
  confirmRegion: async (region) => {
    const full = get().full;
    let marked: Blob | null = full;
    if (full && region) {
      try { marked = await drawMarkedScreenshot(full, region); } catch { marked = full; }
    }
    set({ phase: 'form', region, marked });
  },
  close: () => set({ phase: 'idle', kind: 'bug', full: null, marked: null, region: null }),
}));

export function openBugReport() {
  useBugReport.getState().start();
}

/** Mounted once in the shell. */
export default function BugReportHost() {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  const isStaff = role === 'admin' || role === 'agent';
  const phase = useBugReport((s) => s.phase);
  const kind = useBugReport((s) => s.kind);
  const full = useBugReport((s) => s.full);
  const marked = useBugReport((s) => s.marked);
  const region = useBugReport((s) => s.region);
  const handleClose = useCallback(() => useBugReport.getState().close(), []);

  // Alt+B opens the report (OTE employees).
  useEffect(() => {
    if (!isStaff) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey && (e.code === 'KeyB' || e.key.toLowerCase() === 'b')) {
        e.preventDefault();
        openBugReport();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isStaff]);

  return (
    <>
      {isStaff && phase === 'idle' && (
        <button
          type="button"
          onClick={openBugReport}
          aria-label={t('bugFlow.fabLabel')}
          title={t('bugFlow.fabLabel')}
          data-testid="bug-fab"
          data-html2canvas-ignore="true"
          className="bug-fab fixed end-0 top-[45%] z-nav flex h-11 w-8 items-center justify-center rounded-s-lg border border-e-0 border-border bg-surface text-text-3 opacity-70 shadow-sm transition-opacity hover:opacity-100 focus-visible:opacity-100"
        >
          <Bug size={16} aria-hidden="true" />
        </button>
      )}
      {phase === 'choose' && (
        <Dialog open onClose={handleClose} title={t('bugFlow.chooseTitle')} size="sm">
          <div className="grid gap-3" data-testid="bug-kind-chooser">
            <button type="button" className="btn btn-secondary justify-start gap-3 py-3 text-start" data-testid="choose-bug" onClick={() => { void useBugReport.getState().choose('bug'); }}>
              <Bug size={18} aria-hidden="true" />
              <span><span className="block font-medium">{t('bugFlow.chooseBug')}</span><span className="block text-xs text-text-2">{t('bugFlow.chooseBugHint')}</span></span>
            </button>
            <button type="button" className="btn btn-secondary justify-start gap-3 py-3 text-start" data-testid="choose-suggestion" onClick={() => { void useBugReport.getState().choose('suggestion'); }}>
              <Lightbulb size={18} aria-hidden="true" />
              <span><span className="block font-medium">{t('bugFlow.chooseSuggestion')}</span><span className="block text-xs text-text-2">{t('bugFlow.chooseSuggestionHint')}</span></span>
            </button>
          </div>
        </Dialog>
      )}
      {phase === 'select' && full && (
        <RegionSelector image={full} onConfirm={(r) => { void useBugReport.getState().confirmRegion(r); }} onCancel={handleClose} />
      )}
      {phase === 'form' && (
        <BugReportModal kind={kind} onClose={handleClose} fullScreenshot={full} markedScreenshot={marked} region={region} />
      )}
    </>
  );
}

/** Busy flag for the menu item (capturing the screenshot). */
export function useBugReportBusy(): boolean {
  return useBugReport((s) => s.phase === 'capturing');
}
